// Settings — workspace name, members, integrations. Visual stub.

import {
  Building2,
  FlaskConical,
  Plug,
  User as UserIcon,
  Users,
} from "lucide-react";
import { useState } from "react";

import { TemplatesEditor } from "@/components/TemplatesEditor";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

const MEMBERS = [
  { id: "u1", name: "Bjarne Desmet", email: "bjarne@inku.tech", role: "Owner", avatar: "BJ" },
  { id: "u2", name: "Diyana Stoyanova", email: "diyana@inku.tech", role: "Admin", avatar: "DS" },
  { id: "u3", name: "Cécile De Smet", email: "cecile@loopfoods.com", role: "Editor", avatar: "CD" },
  { id: "u4", name: "Pieter Vermeulen", email: "pieter@loopfoods.com", role: "Editor", avatar: "PV" },
];

const INTEGRATIONS = [
  { id: "shopify", name: "Shopify", desc: "Pull product catalog + sales data", connected: true },
  { id: "meta", name: "Meta Ads", desc: "Connect Facebook + Instagram ads", connected: true },
  { id: "tiktok", name: "TikTok Ads", desc: "Connect TikTok ads manager", connected: false },
  { id: "google", name: "Google Analytics", desc: "Web traffic + conversion data", connected: false },
  { id: "klaviyo", name: "Klaviyo", desc: "Email campaign performance", connected: false },
  { id: "stripe", name: "Stripe", desc: "Revenue + subscriber data", connected: true },
];

export function SettingsPage() {
  const { setToast, me, useBrandGraph, setUseBrandGraph } = useStore();
  const [workspaceName, setWorkspaceName] = useState(me?.org ?? "Loop Foods");

  return (
    <div className="mx-auto w-full max-w-[1100px] px-6 md:px-10 py-12 space-y-12">
      <div className="space-y-2">
        <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
          Workspace
        </p>
        <h1 className="font-freight text-5xl font-light tracking-tight leading-none">
          Settings
        </h1>
        <p className="text-muted-foreground max-w-xl">
          Workspace, members, and integrations. The agent reads from
          everything connected here.
        </p>
      </div>

      {/* Demo controls — knobs you only want to flip mid-demo. Kept
          near the top so they're easy to find when a presentation is
          about to go sideways. */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <FlaskConical className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Demo controls
          </h2>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
          <ToggleRow
            label="Use brand knowledge graph"
            description="When off, the agent does NOT query Neo4j for tenant voice / brand codes / past performance. Flip this off for demos against tenants without a populated graph — the LLM stops wasting turns on empty queries."
            checked={useBrandGraph}
            onChange={setUseBrandGraph}
            offHint="Engine OFF · agent runs text-only without graph context"
          />
        </div>
      </section>

      {/* Briefing templates */}
      <TemplatesEditor />

      {/* Workspace */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Workspace
          </h2>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
          <Field label="Name" value={workspaceName} onChange={setWorkspaceName} />
          <Field label="Slug" value="loop-foods" disabled />
          <Field label="Plan" value="Team" disabled />
          <div className="flex justify-end">
            <Button
              onClick={() =>
                setToast({
                  kind: "ok",
                  text: "Workspace updates are not wired up in this demo.",
                })
              }
            >
              Save changes
            </Button>
          </div>
        </div>
      </section>

      {/* Members */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-freight text-2xl font-light tracking-tight">
              Members
            </h2>
            <span className="font-mono text-xs text-muted-foreground">
              {String(MEMBERS.length).padStart(2, "0")}
            </span>
          </div>
          <Button
            onClick={() =>
              setToast({
                kind: "ok",
                text: "Inviting members is not wired up in this demo.",
              })
            }
            size="sm"
            variant="outline"
            className="gap-1.5"
          >
            Invite
          </Button>
        </div>
        <div className="rounded-lg border border-border bg-card overflow-hidden shadow-card">
          {MEMBERS.map((m, i) => (
            <div
              key={m.id}
              className={cn(
                "px-5 py-3.5 flex items-center gap-4",
                i < MEMBERS.length - 1 && "border-b border-border"
              )}
            >
              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                {m.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {m.role}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <Plug className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Integrations
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {INTEGRATIONS.map((it) => (
            <div
              key={it.id}
              className="rounded-lg border border-border bg-card p-4 shadow-card flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{it.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {it.desc}
                </p>
              </div>
              {it.connected ? (
                <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-green-100 text-green-900 dark:bg-green-950/40 dark:text-green-200">
                  Connected
                </span>
              ) : (
                <Button
                  onClick={() =>
                    setToast({
                      kind: "ok",
                      text: `${it.name} integration not wired up in this demo.`,
                    })
                  }
                  size="sm"
                  variant="outline"
                >
                  Connect
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Profile */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <UserIcon className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Profile
          </h2>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
          <Field label="Name" value={me?.name ?? ""} disabled />
          <Field label="Email" value={me?.email ?? "bjarne@inku.tech"} disabled />
          <p className="text-xs text-muted-foreground/70">
            Profile editing is not available in this demo.
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled || !onChange}
        className={cn(
          "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "outline-none transition-shadow input-focus-glow",
          (disabled || !onChange) && "bg-muted/40 text-muted-foreground cursor-default"
        )}
      />
    </label>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  offHint,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Status line shown ONLY when the toggle is off. Helps the
   * presenter remember they flipped something mid-demo. */
  offHint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="space-y-1 flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {description}
        </p>
        {!checked && offHint && (
          <p className="text-[11px] font-mono uppercase tracking-wider text-amber-700 dark:text-amber-300 pt-1">
            ● {offHint}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full",
          "transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          checked ? "bg-foreground" : "bg-muted border border-border"
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-background shadow-sm",
            "transition-transform duration-200",
            checked ? "translate-x-6" : "translate-x-1"
          )}
        />
      </button>
    </div>
  );
}
